import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import DropDownOA from '../../common/dropDownOA'
import { Item } from '../../common/snippets'
import { Box, Button } from '@mui/material'
import * as sx from '../../common/stylesInline/report_daily_branch'
import { monthNamesList } from '../GeneralSettings/ReportsPage/gsReportsConst'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import { urls } from '../../common/constants'
import useToken from '../../helpers/useTokens'
import { useAtom, useSetAtom } from 'jotai'
import { GetRequest } from '../../common/api'
import { ReportHeading } from './common/ReportHeading'
import { CargoRoleRightsAtom } from '../../common/jotaiState/commonStateAtoms'
import { getMonthlyTargetHeader } from '../dashBoard/constants'
import { useSearchParams } from 'react-router-dom'
import { downloadPDF } from './analysisReport/pdfAutotable'
import { getDecryptedReportParams } from '../../common/loginDecode'
import { GT_COMPANY_ID, GUJRAT_COMPANY_ID, STA_COMPANY_ID } from '../../../helper/companyIdhardcode'
const reportName = "Monthly Target Report"

const branchTypeGroups = [
    { key: 'own', label: 'Own' },
    { key: 'franchisee', label: 'Franchisee' },
]
const baseTotalFields = ['TargetAmount', 'TillTodayTarget', 'Acheived', 'AchievementPercentage']
const gujratTotalFields = ['TargetRemain', 'PerDayAvg', 'RemainingPercentage']
const baseAmountFields = ['TargetAmount', 'TillTodayTarget', 'Acheived']
const gujratAmountFields = ['TargetRemain', 'PerDayAvg']
const percentFields = ['AchievementPercentage', 'RemainingPercentage']

const getNumber = (value) => {
    const numberValue = Number(String(value ?? 0).replace(/,/g, '').replace('%', ''))
    return Number.isFinite(numberValue) ? numberValue : 0
}

const formatTotalValue = (field, value, amountFields) => {
    if (percentFields.includes(field)) {
        return `${value.toFixed(2)}%`
    }
    return amountFields.includes(field) ? Math.round(value) : value
}

const formatPercentValue = (value) =>
    value || value === 0 ? `${getNumber(value).toFixed(2)}%` : value

const formatEntryRow = (item, index) => ({
    ...item,
    rank: index + 1,
    dataType: 'entry',
    AchievementPercentage: formatPercentValue(item.AchievementPercentage),
    RemainingPercentage: formatPercentValue(item.RemainingPercentage),
})

const getTotalRow = (label, rows, headerLength, totalFields, amountFields) => {
    const totals = totalFields.reduce((acc, field) => {
        const total = rows.reduce((sum, row) => sum + getNumber(row[field]), 0)
        acc[field] = formatTotalValue(field, total, amountFields)
        return acc
    }, {})
    const percentage = totals.TargetAmount !== 0 ? (totals.Acheived / totals.TargetAmount) * 100 : 0;
    totals.AchievementPercentage = `${percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(2)}%`;
    if (totalFields.includes('RemainingPercentage')) {
        const RemainingPercentage = totals.TargetAmount !== 0
            ? (getNumber(totals.TargetRemain) / getNumber(totals.TargetAmount)) * 100
            : 0
        totals.RemainingPercentage = `${RemainingPercentage % 1 === 0 ? RemainingPercentage.toFixed(0) : RemainingPercentage.toFixed(2)}%`
    }
    return {
        ...totals,
        rank: label,
        dataType: 'total',
        align: { rank: 'right' },
        colSpan: { rank: headerLength - totalFields.length },
    }
}

const formatData = (rows, headers, totalFields, amountFields) => {
    const formattedRows = []
    const headerLength = headers.length
    const groupedEntryRows = []

    branchTypeGroups.forEach(({ key, label }) => {
        const groupRows = rows
            .filter((item) => String(item.CompanyType || '').trim().toLowerCase() === key).sort((a, b) => getNumber(b.AchievementPercentage) - getNumber(a.AchievementPercentage))
            .map(formatEntryRow)
        if (!groupRows.length) return

        groupedEntryRows.push(groupRows)
        formattedRows.push({
            rank: `Branch Type: ${label}`,
            dataType: 'subHeader',
            align: { rank: 'left' },
            colSpan: { rank: headerLength },
        })
        formattedRows.push(...groupRows)
        formattedRows.push(getTotalRow(`Total: Branch Type: ${label}`, groupRows, headerLength, totalFields, amountFields))
    })

    if (groupedEntryRows.length === branchTypeGroups.length) {
        formattedRows.push(getTotalRow('Total: Branch Type: Own + Franchisee', groupedEntryRows.flat(), headerLength, totalFields, amountFields))
    }

    return formattedRows
}

const openViewReport = (reportPayload) => {
    const rid = Date.now().toString()

    if (!window.generatedReports) {
        window.generatedReports = {}
    }

    window.generatedReports[rid] = reportPayload
    window.open(`/view-report?rid=${rid}`, '_blank')
}

export const MonthlyTargetReport = () => {
    const [urlSearchParams] = useSearchParams()
  const searchParams = getDecryptedReportParams(urlSearchParams)
    let { loginData = {} } = useToken()
    let isFromUrl = false
    if (!loginData) {
        loginData = {}
        loginData.intCompanyID = Number(searchParams.get('C') || 0)
        loginData.intBranchCityID = Number(searchParams.get('CT') || 0)
        loginData.strCityName = searchParams.get('CTN') || ''
        loginData.intUserID = Number(searchParams.get('U') || 0)
        loginData.intBranchID = Number(searchParams.get('B') || 0)
        loginData.strCompanyName = searchParams.get('CN') || ''
        loginData.strUserName = searchParams.get('UN') || ''
        loginData.strBranchName = searchParams.get('BN') || ''
        loginData.IsBranchAdmin = Number(searchParams.get('IBA') || 0)
        loginData.IsMTPLAdmin = Number(searchParams.get('IMPTLA') || 0)
        loginData.IsAdmin = Number(searchParams.get('IA') || 0)
        isFromUrl = true
    }
    const { intCompanyID, intBranchID } = loginData
    const isGT = intCompanyID === GT_COMPANY_ID
    const isSta = intCompanyID === STA_COMPANY_ID
    const isGujrat = intCompanyID === GUJRAT_COMPANY_ID
    const totalFields = isGujrat || isSta ? [...baseTotalFields, ...gujratTotalFields] : baseTotalFields
    const amountFields = isGujrat || isSta ? [...baseAmountFields, ...gujratAmountFields] : baseAmountFields
    const monthlyTargetHeader = getMonthlyTargetHeader({}, intCompanyID)
    const reportHeaders = isGT || isSta
        ? monthlyTargetHeader.map((col) => {
            if (col.field === 'rank') return { ...col, weight: 0.5 }
            if (col.field === 'CompanyType') return { ...col, weight: 0.7 }
            if (isGT && totalFields.includes(col.field)) return { ...col, align: 'left' }
            return col
        })
        : monthlyTargetHeader
    const setLoader = useSetAtom(globalLoaderAtom)
    const setAlert = useSetAtom(alertAtom)
    const currentYear = moment().year()
    const currentMonth = moment().month() + 1
    const [yearList, setYearList] = useState([])
    const [year, setYear] = useState({ year: currentYear })
    const [month, setMonth] = useState(monthNamesList[currentMonth - 1])
    const [branches, setBranches] = useState([])
    const [branch, setBranch] = useState({})
    const [roleRights] = useAtom(CargoRoleRightsAtom)
    const RoleRights = roleRights?.Table1?.[0] || {}
    const { DashboardAccessLevel } = RoleRights || {}
    const criteriaHeaders = [{
        header: 'Year',
        field: 'year',
        textAlign: 'left'
    }, {
        header: 'Month',
        field: 'month',
        textAlign: 'left'
    }, {
        header: 'Branch',
        field: 'branch',
        textAlign: 'right'
    }]
    const criteriaData = [{
        year: year?.year,
        month: month?.name,
        branch: branch?.BranchName,
        dataType: 'entry',
    }]

    const monthList = useMemo(() => {
        if (year?.year === currentYear) {
            return monthNamesList.filter((monthData) => monthData.id <= currentMonth).reverse()
        }
        return [...monthNamesList].reverse()
    }, [currentMonth, currentYear, year?.year])
    useEffect(() => {
        const years = []
        for (let i = currentYear; i > currentYear - 3; i--) {
            years.push({ year: i })
        }
        setYearList(years)
    }, [currentYear])
    useEffect(() => {
        if (!monthList.length) return
        if (!month || !monthList.some((monthData) => monthData.id === month.id)) {
            setMonth(monthList[0])
        }
    }, [month, monthList])
    const resetBranchFilter = () => {
        setBranches([])
        setBranch({})
    }
    const handleClick1 = () => {
        setLoader(true)
        const data = {
            CompanyID: intCompanyID,
            DateFrom: `${year?.year}-${month?.id.toString().padStart(2, '0')}-01`,
            BranchID: 0
        }
        GetRequest(urls.GetMonthlyWiseTarget, data)
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Request Failed')
                }
                if (!Array.isArray(res.Data.Table) || res.Data.Table.length === 0) {
                    throw new Error('No Data Found')
                }
                let list = res.Data.Table
                if (list.length > 1) {
                    list = [{ BranchID: 0, BranchName: 'All Branches' }, ...list]
                }
                const loginBranch = list.find((item) => item.BranchID === intBranchID)
                setBranch(loginBranch || list[0] || {})
                setBranches(list)
            })
            .catch((err) => {
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
            .finally(() => setLoader(false))

    }
    const handleClick2 = (isPdf = false) => {
        if (!branch?.BranchName) {
            setAlert({ show: true, msg: 'Please select a branch', nextId: '' })
            return
        }
        setLoader(true)
        const data = {
            CompanyID: intCompanyID,
            DateFrom: `${year?.year}-${month?.id.toString().padStart(2, '0')}-01`,
            BranchID: branch?.BranchID || 0
        }
        GetRequest(urls.GetMonthlyWiseTarget, data)
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Request Failed')
                }
                if (!Array.isArray(res.Data.Table1) || res.Data.Table1.length === 0) {
                    throw new Error('No Data Found')
                }
                const formattedData = formatData(res.Data.Table1, reportHeaders, totalFields, amountFields)
                const reportPayload = {
                    reportName,
                    criteriaHeaders,
                    criteriaData,
                    tableData: formattedData,
                    mainHeader: reportHeaders,
                    isIncreFontSize: isGT || isSta,
                }

                if (isPdf) {
                    downloadPDF({
                        reportName,
                        criteriaHeaders,
                        criteriaData,
                        tableData: formattedData,
                        visibleColumns: reportHeaders,
                        loginData,
                        hideHeader: false,
                    })
                    return
                }

                openViewReport(reportPayload)
            })
            .catch((err) => {
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
            .finally(() => setLoader(false))

    }
    return (
        <Item sx={{ padding: '0' }} className="reports">
            {isFromUrl && <style>{`.global-header { display: none; } #root { zoom: 1.2; }`}</style>}
            {!isFromUrl && <ReportHeading reportName={reportName} />}
            <Box sx={{ ...sx.reportFlex, gap: isFromUrl ? '15px' : '10px' }} style={{ background: '#fff', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                <Item sx={isFromUrl ? sx.itemsxGrow : sx.itemsx} className="dashboard-branch">
                    <DropDownOA
                        id='mtYear'
                        defaultvalue={year?.year}
                        label='Year'
                        prevId='mtMonthName'
                        nextId='mtBranchType'
                        data={yearList}
                        dataKey={`year`}
                        idKey={`year`}
                        onSelect={(val) => {
                            setYear(val)
                            resetBranchFilter()
                        }}
                        width={'100%'}
                    />
                </Item>

                <Item sx={isFromUrl ? { ...sx.itemsxGrow, width: '60%' } : sx.itemsx} className="dashboard-branch">
                    <DropDownOA
                        id='mtMonthName'
                        defaultvalue={month?.name}
                        label='Month'
                        prevId=''
                        nextId='mtYear'
                        data={monthList}
                        dataKey={`name`}
                        idKey={`id`}
                        onSelect={(val) => {
                            setMonth(val)
                            resetBranchFilter()
                        }}
                    />
                </Item>
                <Item id="generateDiv" tabIndex={0} sx={sx.itemsx}>
                    <Button
                        id="showDashBoard"
                        variant="contained"
                        className="btn btn-primary"
                        autoFocus={false}
                        onClick={handleClick1}
                        sx={{
                            width: '100%',
                            letterSpacing: '2px',
                            height: '40px',
                        }}
                    >
                        GO
                    </Button>
                </Item>
                <Item sx={isFromUrl ? sx.itemsxGrow : sx.itemsx} className="dashboard-branch">
                    <Item sx={isFromUrl ? sx.itemsxGrow : sx.itemsx} className="dashboard-branch">
                        <DropDownOA
                            id="selectBranch"
                            label={'Select Branch'}
                            defaultvalue={branch?.BranchName || ''}
                            onSelect={(val) => {
                                setBranch(val)
                            }}
                            dataKey={'BranchName'}
                            idKey={'BranchID'}
                            data={branches}
                            width="100%"
                            listWidth={'280px'}
                            disabled={!isFromUrl && DashboardAccessLevel < 2}
                        />
                    </Item>
                </Item>
                <Item id="generateDiv" tabIndex={0} sx={isFromUrl ? sx.itemsxGrow : sx.itemsx}>
                    <Button
                        id="showDashBoardLoad"
                        variant="contained"
                        className="btn btn-primary"
                        autoFocus={false}
                        onClick={() => handleClick2(isFromUrl)}
                        sx={{
                            width: '100%',
                            letterSpacing: '2px',
                            height: '40px',
                        }}
                    >
                        {isFromUrl ? 'DOWNLOAD PDF' : 'Load'}
                    </Button>
                </Item>
            </Box>
        </Item>
    )
}
